import React, {useState, useEffect} from "react";
import {Form, Button, Card, CardBody, CardHeader, CardTitle, Row, Col, Spinner, Alert} from "reactstrap";
import {confirmSignUp, resendSignUpCode} from "aws-amplify/auth";
import {useNavigate, useLocation} from "react-router-dom";
import AuthInput from "./AuthInput";
import AuthError from "./AuthError";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {rateLimiter} from "../../utils/rateLimiter";

interface LocationState {
  email?: string;
}

interface VerificationFormProps {
  email?: string;
}

const VerificationForm: React.FC<VerificationFormProps> = ({email: propEmail}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  // Get email from prop, state, localStorage, or empty string
  const [email, setEmail] = useState(() => {
    return propEmail || state?.email || localStorage.getItem("pendingVerificationEmail") || "";
  });

  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    code?: string;
  }>({});

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // If no email is available, redirect to sign in
  useEffect(() => {
    if (!email) {
      navigate("/auth/signin");
    }
  }, [email, navigate]);

  // Check rate limit status on component mount and email change
  useEffect(() => {
    if (email) {
      const rateLimitKey = `verify_${email.toLowerCase()}`;
      const status = rateLimiter.checkRateLimit(rateLimitKey, {
        maxAttempts: 5,
        windowMs: 15 * 60 * 1000, // 15 minutes
        blockDurationMs: 30 * 60 * 1000 // 30 minutes
      });

      setIsRateLimited(status.isBlocked);
      if (status.isBlocked) {
        setRateLimitMessage(status.message || null);
      } else if (status.remainingAttempts < 3) {
        setRateLimitMessage(status.message || null);
      } else {
        setRateLimitMessage(null);
      }
    }
  }, [email]);

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!email) {
      errors.email = "Email is required";
    }

    if (!verificationCode) {
      errors.code = "Verification code is required";
    } else if (verificationCode.length !== 6) {
      errors.code = "Verification code must be 6 digits";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check rate limit before attempting verification
    const rateLimitKey = `verify_${email.toLowerCase()}`;
    const rateLimitStatus = rateLimiter.checkRateLimit(rateLimitKey, {
      maxAttempts: 5,
      windowMs: 15 * 60 * 1000,
      blockDurationMs: 30 * 60 * 1000
    });

    if (rateLimitStatus.isBlocked) {
      setError(rateLimitStatus.message || "Too many attempts. Please try again later.");
      setIsRateLimited(true);
      setRateLimitMessage(rateLimitStatus.message || null);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const {isSignUpComplete} = await confirmSignUp({
        username: email.toLowerCase(),
        confirmationCode: verificationCode
      });

      if (isSignUpComplete) {
        // Clear the stored email and rate limit
        localStorage.removeItem("pendingVerificationEmail");
        rateLimiter.clearRateLimit(rateLimitKey);

        setSuccessMessage("Email verified successfully! Redirecting to sign in...");

        // Redirect to sign in after a short delay
        setTimeout(() => {
          navigate("/auth/signin", {
            state: {
              message: "Your account has been verified. Please sign in.",
              email: email.toLowerCase()
            }
          });
        }, 2000);
      }
    } catch (err: any) {
      // Record failed attempt for rate limiting
      const attemptStatus = rateLimiter.recordAttempt(rateLimitKey, {
        maxAttempts: 5,
        windowMs: 15 * 60 * 1000,
        blockDurationMs: 30 * 60 * 1000
      });

      if (attemptStatus.isNowBlocked) {
        setIsRateLimited(true);
        setRateLimitMessage(attemptStatus.message || null);
      } else if (attemptStatus.remainingAttempts < 3) {
        setRateLimitMessage(attemptStatus.message || null);
      }
      // console.error("Verification error:", err);

      // Handle specific Cognito errors
      if (err.name === "CodeMismatchException") {
        setError("Invalid verification code. Please check and try again.");
      } else if (err.name === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new code.");
      } else if (err.name === "NotAuthorizedException") {
        setError("This account has already been verified. Please sign in.");
      } else if (err.name === "AliasExistsException") {
        setError("An account with this email already exists and is verified.");
      } else {
        setError(err.message || "An error occurred during verification. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError("Email is required to resend the code");
      return;
    }

    // Check rate limit for resend attempts
    const resendRateLimitKey = `resend_${email.toLowerCase()}`;
    const resendStatus = rateLimiter.checkRateLimit(resendRateLimitKey, {
      maxAttempts: 3,
      windowMs: 60 * 60 * 1000, // 1 hour
      blockDurationMs: 60 * 60 * 1000 // 1 hour block
    });

    if (resendStatus.isBlocked) {
      setError(resendStatus.message || "Too many resend attempts. Please try again later.");
      return;
    }

    setResending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await resendSignUpCode({
        username: email.toLowerCase()
      });

      // Record successful resend attempt
      rateLimiter.recordAttempt(resendRateLimitKey, {
        maxAttempts: 3,
        windowMs: 60 * 60 * 1000,
        blockDurationMs: 60 * 60 * 1000
      });

      setSuccessMessage("Verification code sent! Please check your email.");
      setResendCooldown(60); // 60 second cooldown
      setVerificationCode(""); // Clear the old code
    } catch (err: any) {
      // Record failed resend attempt
      rateLimiter.recordAttempt(resendRateLimitKey, {
        maxAttempts: 3,
        windowMs: 60 * 60 * 1000,
        blockDurationMs: 60 * 60 * 1000
      });
      // console.error("Resend code error:", err);

      if (err.name === "LimitExceededException") {
        setError("Too many attempts. Please wait a moment before trying again.");
        setResendCooldown(120); // Longer cooldown for rate limiting
      } else if (err.name === "InvalidParameterException") {
        setError("Invalid email address. Please check and try again.");
      } else if (err.name === "NotAuthorizedException") {
        setError("This account has already been verified. Please sign in.");
      } else {
        setError(err.message || "Failed to resend code. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    // Update localStorage with new email
    if (newEmail) {
      localStorage.setItem("pendingVerificationEmail", newEmail);
    }
    if (validationErrors.email) {
      setValidationErrors({...validationErrors, email: undefined});
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <Card className="auth-card">
          <CardHeader data-color={color}>
            <CardTitle tag="h3">Verify Your Email</CardTitle>
          </CardHeader>
          <CardBody>
            <Alert color="info" className="mb-4">
              <span className="alert-inner--icon">
                <i className="tim-icons icon-email-85" />
              </span>
              <span className="alert-inner--text ml-1">
                We've sent a verification code to your email address. Please enter the 6-digit code below.
              </span>
            </Alert>

            <Form onSubmit={handleSubmit}>
              <AuthError error={error} onClose={() => setError(null)} />

              {rateLimitMessage && !error && (
                <Alert color={isRateLimited ? "danger" : "warning"}>
                  <span className="alert-inner--icon">
                    <i className={`tim-icons icon-${isRateLimited ? "alert-circle-exc" : "bulb-63"}`} />
                  </span>
                  <span className="alert-inner--text ml-1">{rateLimitMessage}</span>
                </Alert>
              )}

              {successMessage && (
                <Alert color="success">
                  <span className="alert-inner--icon">
                    <i className="tim-icons icon-check-2" />
                  </span>
                  <span className="alert-inner--text ml-1">{successMessage}</span>
                </Alert>
              )}

              <AuthInput
                id="email"
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                error={validationErrors.email}
                required
                autoComplete="email"
                disabled={loading}
              />

              <AuthInput
                id="verificationCode"
                label="Verification Code"
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 6) {
                    setVerificationCode(value);
                    if (validationErrors.code) {
                      setValidationErrors({...validationErrors, code: undefined});
                    }
                  }
                }}
                placeholder="Enter 6-digit code"
                error={validationErrors.code}
                required
                maxLength={6}
                pattern="[0-9]{6}"
                disabled={loading}
                onKeyPress={handleKeyPress}
              />

              <Row className="mt-4">
                <Col>
                  <Button
                    color="primary"
                    type="submit"
                    block
                    disabled={loading || !!successMessage || isRateLimited}
                    data-color={color}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <span>Verify Email</span>
                    )}
                  </Button>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col>
                  <Button
                    color="secondary"
                    outline
                    block
                    onClick={handleResendCode}
                    disabled={resending || resendCooldown > 0 || !!successMessage}
                  >
                    {resending ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        <span>Sending...</span>
                      </>
                    ) : resendCooldown > 0 ? (
                      <span>Resend Code ({resendCooldown}s)</span>
                    ) : (
                      <span>Resend Code</span>
                    )}
                  </Button>
                </Col>
              </Row>

              <div className="text-center mt-3">
                <p className="text-muted small">
                  Didn't receive the code? Check your spam folder or click "Resend Code" above.
                </p>
              </div>
            </Form>
          </CardBody>
        </Card>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default VerificationForm;
